import axiosInstance from '../axiosInstance';
import { executePrivateGraphQl, graphQlUrl, publicGraphQlUrl, privateGraphQlUrl, resolveGraphQlMeta } from '../utilities';

const USER_ID_GRAPHQL_SCALAR = graphQlUrl === publicGraphQlUrl ? 'ID!' : 'String!';

const getGraphQlErrors = (response) =>
  Array.isArray(response?.data?.errors) ? response.data.errors :
    Array.isArray(response?.errors) ? response.errors : [];
const getGraphQlErrorMessage = (response, fallback) =>
  getGraphQlErrors(response).map((error) => error?.message).filter(Boolean).join(', ') || fallback;

const EMAIL_INTEGRATION_OAUTH_CALLBACK_PATH = '/api/core/integrations/email/oauth/callback';
const PENDING_OAUTH_CALLBACK_CONTEXT_KEY = 'emailOAuthPendingCallbackContext';

export const EMAIL_PROVIDERS = {
  MICROSOFT: 'microsoft',
  GOOGLE: 'google',
  YAHOO: 'yahoo',
};

const FINALIZE_MUTATION_BY_PROVIDER = {
  [EMAIL_PROVIDERS.MICROSOFT]: {
    operationName: 'FinalizeMicrosoftEmailIntegration',
    inputType: 'MicrosoftOAuthCallbackInput!',
    fieldName: 'finalizeMicrosoftEmailIntegration',
  },
  [EMAIL_PROVIDERS.GOOGLE]: {
    operationName: 'FinalizeEmailIntegration',
    inputType: 'EmailIntegrationOAuthCallbackInput!',
    fieldName: 'finalizeEmailIntegration',
  },
};

const normalizeString = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const safeParseJson = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const decodeBase64UrlJson = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (normalized.length % 4 || 4)) % 4);
    const decoder =
      typeof window !== 'undefined' && typeof window.atob === 'function'
        ? window.atob.bind(window)
        : null;

    if (!decoder) {
      return null;
    }

    return safeParseJson(decoder(`${normalized}${padding}`));
  } catch (error) {
    return null;
  }
};

const readStorageKey = (storage, key) => {
  try {
    return storage?.getItem?.(key) ?? null;
  } catch (error) {
    return null;
  }
};

const extractOAuthStateContext = (candidate) => {
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  const context = candidate.context && typeof candidate.context === 'object'
    ? extractOAuthStateContext(candidate.context)
    : null;
  const appId = candidate.appId || candidate.app_id || candidate.appID || candidate.applicationId || context?.appId;
  const nonce = candidate.nonce || context?.nonce;
  const returnUrl =
    candidate.returnUrl ||
    candidate.return_url ||
    candidate.redirectUrl ||
    candidate.redirect_url ||
    context?.returnUrl ||
    null;
  const originHost = candidate.originHost || context?.originHost || null;
  const createdAt = Number(candidate.createdAt || context?.createdAt);

  if (!hasValue(appId) && !hasValue(nonce) && !hasValue(returnUrl) && !hasValue(originHost)) {
    return null;
  }

  return {
    appId: hasValue(appId) ? String(appId) : undefined,
    nonce: hasValue(nonce) ? String(nonce) : undefined,
    returnUrl,
    originHost,
    createdAt: Number.isFinite(createdAt) && createdAt > 0 ? createdAt : null,
  };
};

const readPendingOAuthCallbackContext = ({ provider, state } = {}) => {
  if (typeof window === 'undefined') {
    return null;
  }

  const parsed =
    safeParseJson(readStorageKey(window.sessionStorage, PENDING_OAUTH_CALLBACK_CONTEXT_KEY)) ||
    safeParseJson(readStorageKey(window.localStorage, PENDING_OAUTH_CALLBACK_CONTEXT_KEY));

  if (!parsed || parsed.provider !== provider) {
    return null;
  }

  if (hasValue(state) && hasValue(parsed.state) && String(parsed.state) !== String(state)) {
    return null;
  }

  return extractOAuthStateContext(parsed.context || parsed);
};

const toUserAppAccessInput = (accessRecord = {}) => ({
  userAppAccessId: accessRecord.userAppAccessId,
  userId: accessRecord.userId,
  appId: accessRecord.appId,
  accessLevel: accessRecord.accessLevel,
  grantedDate: accessRecord.grantedDate,
  expirationDate: accessRecord.expirationDate,
  lawsuitRecommend: accessRecord.lawsuitRecommend,
  signupRedirectUrl: accessRecord.signupRedirectUrl,
  LitigAIStage: accessRecord.LitigAIStage,
  emailLinked: true,
});

const buildMeta = (userId, appId) =>
  resolveGraphQlMeta({
    userId,
    ...(appId ? { appId } : {}),
  });

export const getEmailIntegrations = async ({ userId, appId } = {}) => {
  const meta = buildMeta(userId, appId);

  const query = `
    query GetEmailIntegrations($userId: String!, $appId: String!) {
      emailIntegrations(userId: $userId, appId: $appId) {
        integrationId
        userId
        appId
        provider
        emailAddress
        status
      }
    }
  `;

  const response = await executePrivateGraphQl(
    query,
    { userId: meta.userId, appId: meta.appId },
    { url: privateGraphQlUrl },
    meta
  );

  if (getGraphQlErrors(response).length > 0) throw new Error(getGraphQlErrorMessage(response, 'Unable to fetch email integrations.'));

  const integrations = response?.data?.data?.emailIntegrations;
  if (!Array.isArray(integrations)) throw new Error('Email integrations were not returned.');
  return integrations;
};

export const getUserAppAccessRecords = async ({ userId, appId } = {}) => {
  const meta = buildMeta(userId, appId);

  const query = `
    query GetUserAppAccess($userId: ${USER_ID_GRAPHQL_SCALAR}, $appId: ${USER_ID_GRAPHQL_SCALAR}) {
      userAppAccess(userId: $userId, appId: $appId) {
        userId
        userAppAccessId
        signupRedirectUrl
        lawsuitRecommend
        grantedDate
        expirationDate
        emailLinked
        appId
        accessLevel
        allowedActiveCases
        LitigAIStage
        userType
      }
    }
  `;

  const response = await executePrivateGraphQl(
    query,
    { userId: meta.userId, appId: meta.appId },
    {},
    meta
  );

  if (getGraphQlErrors(response).length > 0) throw new Error(getGraphQlErrorMessage(response, 'Unable to fetch user app access.'));

  const records = response?.data?.data?.userAppAccess;
  if (!Array.isArray(records)) throw new Error('User app access records were not returned.');
  return records;
};

export const hasConnectedIntegration = (integrations = [], provider) =>
  integrations.some(
    (integration) =>
      normalizeString(integration?.provider) === normalizeString(provider) &&
      normalizeString(integration?.status) === 'connected'
  );

export const hasConnectedMicrosoftIntegration = (integrations = []) =>
  hasConnectedIntegration(integrations, EMAIL_PROVIDERS.MICROSOFT);

export const hasConnectedGoogleIntegration = (integrations = []) =>
  hasConnectedIntegration(integrations, EMAIL_PROVIDERS.GOOGLE);

export const hasConnectedYahooIntegration = (integrations = []) =>
  hasConnectedIntegration(integrations, EMAIL_PROVIDERS.YAHOO);

export const finalizeIntegrationOAuthCallback = async ({
  userId,
  appId,
  provider,
  code,
  state,
} = {}) => {
  if (!userId || !appId || !provider || !code || !state) {
    return null;
  }

  // Microsoft email OAuth is started by the email Lambda, which owns the
  // provider state and PKCE verifier. Finalize it through the same callback
  // endpoint for signed-in and session-free users so Django GraphQL cannot
  // look for a separate state record created by another connect path.
  if (
    provider === EMAIL_PROVIDERS.MICROSOFT ||
    provider === EMAIL_PROVIDERS.YAHOO
  ) {
    return axiosInstance.post(
      EMAIL_INTEGRATION_OAUTH_CALLBACK_PATH,
      {
        provider,
        code,
        state,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }

  const finalizeConfig = FINALIZE_MUTATION_BY_PROVIDER[provider];

  if (!finalizeConfig) {
    throw new Error(`Unsupported email provider: ${provider}`);
  }

  const mutation = `
    mutation ${finalizeConfig.operationName}($input: ${finalizeConfig.inputType}) {
      ${finalizeConfig.fieldName}(input: $input) {
        integration {
          integrationId
          userId
          appId
          provider
          emailAddress
          status
        }
      }
    }
  `;

  const meta = buildMeta(userId, appId);
  const response = await executePrivateGraphQl(
    mutation,
    {
      input: {
        code,
        state,
      },
    },
    {},
    meta
  );

  if (getGraphQlErrors(response).length > 0) throw new Error(getGraphQlErrorMessage(response, `${provider} OAuth finalization failed.`));

  const payload = response?.data?.data?.[finalizeConfig.fieldName] || null;
  const integration = payload?.integration || payload;
  if (!integration) {
    throw new Error(`${provider} OAuth finalization returned no integration payload.`);
  }

  return integration;
};

export const finalizeMicrosoftIntegrationOAuthCallback = async ({
  userId,
  appId,
  code,
  state,
}) =>
  finalizeIntegrationOAuthCallback({
    userId,
    appId,
    provider: EMAIL_PROVIDERS.MICROSOFT,
    code,
    state,
  });

export const finalizeGoogleIntegrationOAuthCallback = async ({
  userId,
  appId,
  code,
  state,
}) =>
  finalizeIntegrationOAuthCallback({
    userId,
    appId,
    provider: EMAIL_PROVIDERS.GOOGLE,
    code,
    state,
  });

export const finalizeYahooIntegrationOAuthCallback = async ({
  userId,
  appId,
  code,
  state,
}) =>
  finalizeIntegrationOAuthCallback({
    userId,
    appId,
    provider: EMAIL_PROVIDERS.YAHOO,
    code,
    state,
  });

export const resolveOAuthStateContext = async ({ provider, state, nonce } = {}) => {
  if (!provider || (!state && !nonce)) {
    return { success: false, context: null };
  }

  const parsedState = safeParseJson(state) || decodeBase64UrlJson(state);
  const stateContext = extractOAuthStateContext(parsedState);

  if (stateContext) {
    return {
      success: true,
      context: {
        ...stateContext,
        nonce: stateContext.nonce || nonce,
      },
      raw: {
        provider,
        state,
        nonce,
        source: 'state',
      },
    };
  }

  const pendingContext = readPendingOAuthCallbackContext({ provider, state });

  if (pendingContext) {
    return {
      success: true,
      context: {
        ...pendingContext,
        nonce: pendingContext.nonce || nonce,
      },
      raw: {
        provider,
        state,
        nonce,
        source: 'pending-callback',
      },
    };
  }

  return {
    success: false,
    context: null,
    raw: {
      provider,
      state,
      nonce,
      reason: 'resolve_oauth_state_not_supported',
    },
  };
};

export const resolveMicrosoftOAuthStateContext = async ({ state, nonce } = {}) =>
  resolveOAuthStateContext({
    provider: EMAIL_PROVIDERS.MICROSOFT,
    state,
    nonce,
  });

export const resolveGoogleOAuthStateContext = async ({ state, nonce } = {}) =>
  resolveOAuthStateContext({
    provider: EMAIL_PROVIDERS.GOOGLE,
    state,
    nonce,
  });

export const resolveYahooOAuthStateContext = async ({ state, nonce } = {}) =>
  resolveOAuthStateContext({
    provider: EMAIL_PROVIDERS.YAHOO,
    state,
    nonce,
  });

export const updateUserAppAccessEmailLinked = async (accessRecord = {}, meta = {}) => {
  const payload = toUserAppAccessInput(accessRecord);

  const mutation = `
    mutation UpdateUserAppAccess(
      $userAppAccessId: String!
      $userId: String!
      $appId: String!
      $userAppAccessData: UserAppAccessInput!
    ) {
      updateUserAppAccess(
        userAppAccessId: $userAppAccessId
        userId: $userId
        appId: $appId
        userAppAccessData: $userAppAccessData
      ) {
        userAppAccess {
          userAppAccessId
          userId
          appId
          emailLinked
          allowedActiveCases
        }
      }
    }
  `;

  return executePrivateGraphQl(
    mutation,
    {
      userAppAccessId: accessRecord.userAppAccessId,
      userId: accessRecord.userId,
      appId: accessRecord.appId,
      userAppAccessData: payload,
    },
    {},
    buildMeta(meta.userId, meta.appId)
  );
};
